import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import EavAttributeField from '../components/EavAttributeField';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { findFirstEavError } from '../utils/attributeValidation';

const EmployeeForm = ({ viewOnly = false }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [config, setConfig] = useState([]);
  const [defaultAttributeSet, setDefaultAttributeSet] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    is_super_admin: 0,
    attribute_set_id: null,
    hire_date: '',
    species: 'human',
    status: 1,
    attributes: {},
  });
  const [employeePositions, setEmployeePositions] = useState([]);
  const [supervisorName, setSupervisorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 获取员工默认属性组
  useEffect(() => {
    const fetchDefaultAttributeSet = async () => {
      try {
        const res = await fetch('/api/settings/attribute-sets');
        const data = await res.json();
        if (data.status === 'success' && data.data) {
          const defaultSet = data.data.find(
            set => set.entity_type === 'employee' && set.set_code === 'default'
          );
          if (defaultSet) {
            setDefaultAttributeSet(defaultSet);
            setFormData(prev => ({
              ...prev,
              attribute_set_id: defaultSet.set_id
            }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch default attribute set:', err);
      }
    };
    fetchDefaultAttributeSet();
  }, []);

  useEffect(() => {
    if (id) {
      fetch(`/api/employees/${id}`)
        .then(res => {
          if (!res.ok) throw new Error(`API returned ${res.status}`);
          return res.json();
        })
        .then(async (res) => {
          if (res.status === 'success' && res.data) {
            setFormData(prev => ({
              ...prev,
              ...res.data,
              password: '',
              attribute_set_id: defaultAttributeSet ? defaultAttributeSet.set_id : res.data.attribute_set_id
            }));
            // 查看模式下加载岗位和上级信息
            if (viewOnly && id) {
              // 加载岗位信息
              fetch(`/api/employees/positions?employee_id=${id}`)
                .then(r => r.json())
                .then(posRes => {
                  if (posRes.status === 'success') {
                    setEmployeePositions(posRes.data);
                  }
                }).catch(err => console.error('Failed to fetch positions:', err));
              // 加载直属上级
              if (res.data.supervisor_id) {
                fetch(`/api/employees/${res.data.supervisor_id}`)
                  .then(r => r.json())
                  .then(supRes => {
                    if (supRes.status === 'success' && supRes.data) {
                      const fn = supRes.data.attributes?.first_name || '';
                      const ln = supRes.data.attributes?.last_name || '';
                      setSupervisorName(`${supRes.data.entity_id} - ${fn} - ${ln}`);
                    }
                  }).catch(err => console.error('Failed to fetch supervisor:', err));
              }
            }
          }
        })
        .catch(err => console.error('Failed to fetch employee:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id, defaultAttributeSet]);

  useEffect(() => {
    if (!formData.attribute_set_id) return;
    fetch(`/api/employees/form-config?attribute_set_id=${formData.attribute_set_id}`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`API returned ${res.status}`);
        }
        return res.json();
      })
      .then(res => {
        if (res.status === 'success' && res.data) {
          setConfig(res.data);
        } else {
          console.error('Employee form config API returned error:', res.message);
          setConfig([]);
        }
      })
      .catch(err => {
        console.error('Failed to fetch employee form config:', err);
        setConfig([]);
      });
  }, [formData.attribute_set_id]);

  const handleAttributeChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [code]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (viewOnly) {
      navigate('/employees');
      return;
    }

    // ========== 基础字段必填验证（逐一检查，聚焦第一个错误字段）==========
    const form = e.currentTarget;
    if (!formData.email || !String(formData.email).trim()) {
      notify(t('validation.email_required'), 'error');
      form.querySelector('[name="email"]')?.focus();
      return;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      notify(t('validation.must_be_email'), 'error');
      form.querySelector('[name="email"]')?.focus();
      return;
    }
    if (!id && (!formData.password || !String(formData.password).trim())) {
      notify(t('validation.password_required'), 'error');
      form.querySelector('[name="password"]')?.focus();
      return;
    }
    if (formData.password && String(formData.password).trim().length > 0 && String(formData.password).length < 8) {
      notify(t('validation.password_min_length', { n: 8 }), 'error');
      form.querySelector('[name="password"]')?.focus();
      return;
    }
    // 仅创建时验证 hire_date/species/status（编辑模式下后端已禁止修改这些字段）
    if (!id) {
      if (!formData.hire_date) {
        notify(t('validation.hire_date_required'), 'error');
        form.querySelector('[name="hire_date"]')?.focus();
        return;
      }
      if (!formData.species) {
        notify(t('validation.species_required'), 'error');
        form.querySelector('[name="species"]')?.focus();
        return;
      }
      if (formData.status === undefined || formData.status === null || formData.status === '') {
        notify(t('validation.status_required'), 'error');
        form.querySelector('[name="status"]')?.focus();
        return;
      }
    }

    // EAV 属性必填验证 + input_validation 格式验证（使用共享工具）
    const eavError = findFirstEavError(config, formData.attributes, t);
    if (eavError) {
      notify(eavError, 'error');
      return;
    }

    const endpoint = id ? `/api/employees/${id}/edit` : '/api/employees/add';
    const method = id ? 'PUT' : 'POST';

    setIsSubmitting(true);
    try {
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        setTimeout(() => navigate('/employees'), 1000);
      } else {
        notify(t('common.error') + ': ' + data.message, 'error');
      }
    } catch (err) {
      notify(t('common.error') + ': ' + err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="text-center p-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{viewOnly ? t('employees.view_employee') : (id ? t('employees.edit_employee') : t('employees.new_employee'))}</h1>
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-200 rounded-full">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} noValidate className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
        <div className={viewOnly ? 'pointer-events-none select-none' : ''}>
        <div className="space-y-8">
          {/* 基本信息 */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employees.email')} <span className="text-red-500">*</span></label>
            <input 
              type="email"
              name="email"
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              required
              disabled={viewOnly}
            />
          </div>
          <PasswordInput 
            name="password"
            label={t('employees.password') + ' *'} 
            value={formData.password} 
            onChange={e => setFormData({...formData, password: e.target.value})} 
            required={!id} 
            placeholder={id ? "Change password if needed" : t('employees.password')}
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employees.attr_set')}</label>
            <div className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {defaultAttributeSet ? defaultAttributeSet.set_name : t('common.employee_default_set')}
              <input type="hidden" name="attribute_set_id" value={formData.attribute_set_id || ''} />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('common.only_default_set', {entity: t('entity.employee')})}</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employees.hire_date')} <span className="text-red-500">*</span></label>
            <input 
              type="date"
              name="hire_date"
              className={"w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none bg-transparent " + (id ? "opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-800" : "focus:ring-2 focus:ring-blue-500")}
              value={formData.hire_date}
              onChange={e => setFormData({...formData, hire_date: e.target.value})}
              required
              disabled={viewOnly || !!id}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employees.species')} <span className="text-red-500">*</span></label>
            <select 
              name="species"
              className={"w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none bg-transparent " + (id ? "opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-800" : "focus:ring-2 focus:ring-blue-500")}
              value={formData.species}
              onChange={e => setFormData({...formData, species: e.target.value})}
              disabled={viewOnly || !!id}
              required
            >
              <option value="">{t("employees.select_species")}</option>
              <option value="human">{t("employees.human")}</option>
              <option value="ai">AI</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('employees.status')} <span className="text-red-500">*</span></label>
            <select 
              name="status"
              className={"w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none bg-transparent " + (id ? "opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-800" : "focus:ring-2 focus:ring-blue-500")}
              value={formData.status}
              onChange={e => setFormData({...formData, status: parseInt(e.target.value)})}
              disabled={viewOnly || !!id}
              required
            >
              <option value="">{t("employees.select_status")}</option>
              <option value="1">{t("employees.active")}</option>
              <option value="0">{t("employees.frozen")}</option>
            </select>
          </div>

          {/* 查看模式下展示部门/职位/直属上级 */}
          {viewOnly && employeePositions.length > 0 && (
            <>
              <div className="border-t border-slate-200 dark:border-slate-700 my-6" />
              <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4">{t('employees.position_info')}</h3>
              <div className="space-y-4">
                {employeePositions.map((pos, idx) => (
                  <div key={pos.id || idx} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('employees.department')}</label>
                        <div className="text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                          {pos.department_name || '-'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('employees.position')}</label>
                        <div className="text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                          {pos.position_name}
                          {pos.is_primary ? <span className="text-xs text-blue-500 ml-1">({t('employees.primary_position')})</span> : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('employees.supervisor')}</label>
                  <div className="text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                    {supervisorName || '-'}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* EAV 属性 - 与其他字段混合展示 */}
          {config.length > 0 && config.map(attr => (
            <div key={attr.attribute_code} className="space-y-2">
              <EavAttributeField
                attr={attr}
                value={formData.attributes[attr.attribute_code]}
                onChange={handleAttributeChange}
                viewOnly={viewOnly}
              />
            </div>
          ))}

        </div>

        <div className="flex justify-end gap-3 pt-6 border-t">
          {viewOnly ? (
            <button 
              type="button" 
              onClick={() => navigate(-1)} 
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.close')}
            </button>
          ) : (
            <>
              <button 
                type="button" 
                onClick={() => navigate(-1)} 
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting}
                className={`px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  <><Save size={18} />{id ? t('common.update') : t('common.add')}</>
                )}
              </button>
            </>
          )}
        </div>
        </div>
      </form>
    </div>
  );
};

export default EmployeeForm;
